import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Typography,
  Button,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';

const EmailSends = () => {
  const [sends, setSends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadSends();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, rowsPerPage]);

  const loadSends = async () => {
    try {
      setLoading(true);
      const params = {
        page: page,
        page_size: rowsPerPage,
        ...(statusFilter && { status: statusFilter }),
      };
      const response = await axios.get('/email/sends', { params });
      setSends(response.data.sends);
      setTotal(response.data.total);
    } catch (err) {
      setError('Failed to load email sends');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing page size
  };

  const handleResend = async (sendId) => {
    try {
      const response = await axios.post(`/email/resend/${sendId}`);
      if (response.data.success) {
        setSuccess('Email resent successfully');
        setTimeout(() => setSuccess(''), 5000);
        loadSends();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend email');
      setTimeout(() => setError(''), 5000);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent':
        return 'success';
      case 'failed':
        return 'error';
      case 'pending':
        return 'warning';
      case 'retrying':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0); // Reset to first page when filter changes
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="retrying">Retrying</MenuItem>
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadSends}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tour</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Available Seats</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Sent At</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sends.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No email sends found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sends.map((send) => (
                  <TableRow key={send.id}>
                    <TableCell>{send.tour_name}</TableCell>
                    <TableCell>
                      {send.tour_date} at {send.tour_time}
                    </TableCell>
                    <TableCell>
                      {send.recipient_name ? (
                        <>
                          {send.recipient_name}<br />
                          <Typography variant="caption" color="text.secondary">
                            {send.recipient_email}
                          </Typography>
                        </>
                      ) : (
                        send.recipient_email
                      )}
                    </TableCell>
                    <TableCell>{send.available_seats}</TableCell>
                    <TableCell>
                      <Chip
                        label={send.status}
                        color={getStatusColor(send.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {formatDate(send.sent_at)}
                    </TableCell>
                    <TableCell>
                      {send.error_message ? (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={send.error_message}
                        >
                          {send.error_message}
                        </Typography>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {send.status === 'failed' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleResend(send.id)}
                        >
                          Resend
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[25, 50, 100, 250]}
            labelRowsPerPage="Rows per page:"
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default EmailSends;


